import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import api from "../../lib/api";
import { Card } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";

const EMPTY_FORM = { relationship: "child", first_name: "", last_name: "", birthday: "" };

export default function FamilyFriends() {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/family-members").then(({ data }) => setMembers(data)).catch(() => setMembers([]));

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/family-members", form);
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/family-members/${id}`);
    load();
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Family & Friends</h1>
        <Button size="sm" onClick={() => setShowForm((v) => !v)}>Add member</Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Book appointments on behalf of people close to you.
      </p>

      {showForm && (
        <Card className="mb-6 p-4">
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <select
              value={form.relationship}
              onChange={(e) => setForm({ ...form, relationship: e.target.value })}
              className="h-11 rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-primary"
            >
              <option value="spouse">Spouse</option>
              <option value="child">Child</option>
              <option value="parent">Parent</option>
              <option value="friend">Friend</option>
              <option value="other">Other</option>
            </select>
            <Input placeholder="First name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            <Input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            <Input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} />
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {members.length === 0 ? (
        <p className="text-muted-foreground">No family or friends added yet.</p>
      ) : (
        <div className="space-y-2">
          {members.map((m) => (
            <Card key={m.id} className="flex items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="font-medium text-slate-900">{m.first_name} {m.last_name}</p>
                <p className="text-xs capitalize text-muted-foreground">{m.relationship}</p>
              </div>
              <button onClick={() => remove(m.id)} className="text-muted-foreground hover:text-destructive">
                <Trash2 className="h-4 w-4" />
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
